import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Alert,
  Form,
  Label,
  Input,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import BreadCrumb from "../../Components/Common/BreadCrumb";

const BillingUpgrade = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string>("pro");
  const [billingCycle, setBillingCycle] = useState<string>("monthly");
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const plans = [
    {
      id: "starter",
      name: "Starter",
      monthlyPrice: 9,
      yearlyPrice: 90,
      features: [
        "Up to 5 projects",
        "10GB storage",
        "Email support",
        "Basic analytics",
        "API access",
      ],
      color: "secondary",
      recommended: false,
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: 29,
      yearlyPrice: 290,
      features: [
        "Unlimited projects",
        "100GB storage",
        "Priority support",
        "Advanced analytics",
        "API access",
        "Custom integrations",
        "Team collaboration",
      ],
      color: "primary",
      recommended: true,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      monthlyPrice: 99,
      yearlyPrice: 990,
      features: [
        "Everything in Pro",
        "Unlimited storage",
        "24/7 phone support",
        "Custom analytics",
        "Advanced API access",
        "SSO integration",
        "Dedicated account manager",
        "Custom contracts",
      ],
      color: "success",
      recommended: false,
    },
  ];

  const paymentValidation = useFormik({
    initialValues: {
      cardNumber: "",
      expiryDate: "",
      cvv: "",
      cardName: "",
      billingAddress: "",
      city: "",
      country: "",
      postalCode: "",
    },
    validationSchema: Yup.object({
      cardNumber: Yup.string()
        .required("Card number is required")
        .matches(/^[0-9]{16}$/, "Card number must be 16 digits"),
      expiryDate: Yup.string()
        .required("Expiry date is required")
        .matches(/^(0[1-9]|1[0-2])\/([0-9]{2})$/, "Format: MM/YY"),
      cvv: Yup.string()
        .required("CVV is required")
        .matches(/^[0-9]{3,4}$/, "CVV must be 3-4 digits"),
      cardName: Yup.string().required("Cardholder name is required"),
      billingAddress: Yup.string().required("Billing address is required"),
      city: Yup.string().required("City is required"),
      country: Yup.string().required("Country is required"),
      postalCode: Yup.string().required("Postal code is required"),
    }),
    onSubmit: async (values) => {
      setProcessing(true);
      
      // Simulate payment processing
      setTimeout(() => {
        setProcessing(false);
        setShowPaymentModal(false);
        
        // Navigate back to billing page with success message
        navigate("/billing", { 
          state: { 
            message: `Successfully upgraded to ${plans.find(p => p.id === selectedPlan)?.name} plan!` 
          }
        });
      }, 2000);
    },
  });

  const getPlanPrice = (plan: any) => {
    return billingCycle === "yearly" ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getSavingsPercentage = (plan: any) => {
    return Math.round((1 - (plan.yearlyPrice / 12) / plan.monthlyPrice) * 100);
  };

  const handleUpgrade = () => {
    setShowPaymentModal(true);
  };

  const formatCardNumber = (value: string) => {
    return value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
  };

  document.title = "Upgrade Plan | Velzon - React Admin & Dashboard Template";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Upgrade Plan" pageTitle="Billing" />

          {/* Header */}
          <div className="text-center mb-5">
            <h3 className="mb-3">Choose Your Plan</h3>
            <p className="text-muted">
              Upgrade your plan to unlock more features and grow your business
            </p>

            {/* Billing Toggle */}
            <div className="d-flex justify-content-center mt-4">
              <div className="btn-group" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="billingCycle"
                  id="monthly"
                  checked={billingCycle === "monthly"}
                  onChange={() => setBillingCycle("monthly")}
                />
                <Label className="btn btn-outline-primary" htmlFor="monthly">
                  Monthly
                </Label>

                <input
                  type="radio"
                  className="btn-check"
                  name="billingCycle"
                  id="yearly"
                  checked={billingCycle === "yearly"}
                  onChange={() => setBillingCycle("yearly")}
                />
                <Label className="btn btn-outline-primary" htmlFor="yearly">
                  Yearly
                  <Badge color="success" className="ms-1">
                    Save up to 17%
                  </Badge>
                </Label>
              </div>
            </div>
          </div>

          {/* Pricing Plans */}
          <Row className="justify-content-center">
            {plans.map((plan) => (
              <Col lg={4} md={6} key={plan.id} className="mb-4">
                <Card 
                  className={`h-100 position-relative ${
                    selectedPlan === plan.id ? `border-${plan.color}` : ""
                  } ${plan.recommended ? "border-primary" : ""}`}
                >
                  {plan.recommended && (
                    <div className="position-absolute top-0 start-50 translate-middle">
                      <Badge color="primary" className="fs-12">
                        RECOMMENDED
                      </Badge>
                    </div>
                  )}
                  
                  <CardBody className="p-4">
                    <div className="text-center mb-4">
                      <h4 className="fw-bold mb-1">{plan.name}</h4>
                      <div className="mb-3">
                        <h2 className="fw-bold text-primary mb-0">
                          ${getPlanPrice(plan)}
                          <small className="text-muted fs-13 fw-normal">
                            /{billingCycle === "yearly" ? "year" : "month"}
                          </small>
                        </h2>
                        {billingCycle === "yearly" && (
                          <small className="text-success">
                            Save {getSavingsPercentage(plan)}% annually
                          </small>
                        )}
                      </div>
                    </div>

                    <ul className="list-unstyled vstack gap-2 mb-4">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="d-flex align-items-center">
                          <i className="ri-check-line text-success me-2"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="d-grid gap-2">
                      <input
                        type="radio"
                        className="btn-check"
                        name="selectedPlan"
                        id={plan.id}
                        checked={selectedPlan === plan.id}
                        onChange={() => setSelectedPlan(plan.id)}
                      />
                      <Label
                        className={`btn ${
                          selectedPlan === plan.id
                            ? `btn-${plan.color}`
                            : `btn-outline-${plan.color}`
                        }`}
                        htmlFor={plan.id}
                      >
                        {selectedPlan === plan.id ? "Selected" : "Select Plan"}
                      </Label>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Selected Plan Summary */}
          {selectedPlan && (
            <Card className="mt-4">
              <CardBody>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="mb-1">
                      Selected Plan: {plans.find((p) => p.id === selectedPlan)?.name}
                    </h5>
                    <p className="text-muted mb-0">
                      ${getPlanPrice(plans.find((p) => p.id === selectedPlan)!)} per{" "}
                      {billingCycle === "yearly" ? "year" : "month"}
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end">
                    <Button color="primary" size="lg" onClick={handleUpgrade}>
                      Upgrade Now
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Back to Billing */}
          <div className="text-center mt-4">
            <Link to="/billing" className="btn btn-ghost-secondary">
              <i className="ri-arrow-left-line me-1"></i>
              Back to Billing
            </Link>
          </div>
        </Container>
      </div>

      {/* Payment Modal */}
      <Modal isOpen={showPaymentModal} toggle={() => setShowPaymentModal(false)} size="lg" centered>
        <Form onSubmit={paymentValidation.handleSubmit}>
          <ModalHeader toggle={() => setShowPaymentModal(false)}>
            Complete Your Upgrade
          </ModalHeader>
          <ModalBody>
            <Alert color="info" className="mb-4">
              <div className="d-flex">
                <i className="ri-information-line me-2 mt-1"></i>
                <div>
                  <strong>Order Summary:</strong> {plans.find((p) => p.id === selectedPlan)?.name} Plan - 
                  ${getPlanPrice(plans.find((p) => p.id === selectedPlan)!)} 
                  /{billingCycle === "yearly" ? "year" : "month"}
                </div>
              </div>
            </Alert>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label className="form-label">Card Number</Label>
                  <Input
                    type="text"
                    name="cardNumber"
                    placeholder="1234 5678 9012 3456"
                    value={formatCardNumber(paymentValidation.values.cardNumber)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s/g, '');
                      if (value.length <= 16) {
                        paymentValidation.setFieldValue('cardNumber', value);
                      }
                    }}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.cardNumber && paymentValidation.errors.cardNumber ? true : false}
                  />
                  {paymentValidation.touched.cardNumber && paymentValidation.errors.cardNumber && (
                    <FormFeedback>{paymentValidation.errors.cardNumber}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label className="form-label">Expiry Date</Label>
                  <Input
                    type="text"
                    name="expiryDate"
                    placeholder="MM/YY"
                    value={paymentValidation.values.expiryDate}
                    onChange={paymentValidation.handleChange}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.expiryDate && paymentValidation.errors.expiryDate ? true : false}
                  />
                  {paymentValidation.touched.expiryDate && paymentValidation.errors.expiryDate && (
                    <FormFeedback>{paymentValidation.errors.expiryDate}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <Label className="form-label">CVV</Label>
                  <Input
                    type="text"
                    name="cvv"
                    placeholder="123"
                    value={paymentValidation.values.cvv}
                    onChange={paymentValidation.handleChange}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.cvv && paymentValidation.errors.cvv ? true : false}
                  />
                  {paymentValidation.touched.cvv && paymentValidation.errors.cvv && (
                    <FormFeedback>{paymentValidation.errors.cvv}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <Label className="form-label">Cardholder Name</Label>
              <Input
                type="text"
                name="cardName"
                placeholder="John Doe"
                value={paymentValidation.values.cardName}
                onChange={paymentValidation.handleChange}
                onBlur={paymentValidation.handleBlur}
                invalid={paymentValidation.touched.cardName && paymentValidation.errors.cardName ? true : false}
              />
              {paymentValidation.touched.cardName && paymentValidation.errors.cardName && (
                <FormFeedback>{paymentValidation.errors.cardName}</FormFeedback>
              )}
            </div>

            <h6 className="fw-semibold mb-3">Billing Address</h6>
            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <Label className="form-label">Address</Label>
                  <Input
                    type="text"
                    name="billingAddress"
                    placeholder="123 Main Street"
                    value={paymentValidation.values.billingAddress}
                    onChange={paymentValidation.handleChange}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.billingAddress && paymentValidation.errors.billingAddress ? true : false}
                  />
                  {paymentValidation.touched.billingAddress && paymentValidation.errors.billingAddress && (
                    <FormFeedback>{paymentValidation.errors.billingAddress}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="form-label">City</Label>
                  <Input
                    type="text"
                    name="city"
                    placeholder="New York"
                    value={paymentValidation.values.city}
                    onChange={paymentValidation.handleChange}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.city && paymentValidation.errors.city ? true : false}
                  />
                  {paymentValidation.touched.city && paymentValidation.errors.city && (
                    <FormFeedback>{paymentValidation.errors.city}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="form-label">Country</Label>
                  <Input
                    type="select"
                    name="country"
                    value={paymentValidation.values.country}
                    onChange={paymentValidation.handleChange}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.country && paymentValidation.errors.country ? true : false}
                  >
                    <option value="">Select Country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="AU">Australia</option>
                  </Input>
                  {paymentValidation.touched.country && paymentValidation.errors.country && (
                    <FormFeedback>{paymentValidation.errors.country}</FormFeedback>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <div className="mb-3">
                  <Label className="form-label">Postal Code</Label>
                  <Input
                    type="text"
                    name="postalCode"
                    placeholder="10001"
                    value={paymentValidation.values.postalCode}
                    onChange={paymentValidation.handleChange}
                    onBlur={paymentValidation.handleBlur}
                    invalid={paymentValidation.touched.postalCode && paymentValidation.errors.postalCode ? true : false}
                  />
                  {paymentValidation.touched.postalCode && paymentValidation.errors.postalCode && (
                    <FormFeedback>{paymentValidation.errors.postalCode}</FormFeedback>
                  )}
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button 
              type="button" 
              color="secondary" 
              onClick={() => setShowPaymentModal(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={processing}>
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Processing...
                </>
              ) : (
                `Pay $${getPlanPrice(plans.find((p) => p.id === selectedPlan)!)}`
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default BillingUpgrade;